import {
  getDirectusInstance,
  PropertyAvailability,
  resolveTranslation,
} from "@fhlib/directus";
import { BookingContactFormData } from "@molecules/booking-contact-form/types";
import { PropertyCardProps } from "@molecules/property-card/property-card";
import { SearchForm } from "@organisms/searcher";
import { ReservationsTemplate } from "@templates/reservations";
import { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useCallback } from "react";
import * as dateFns from "date-fns";
import { useTranslation } from "next-i18next";
import Head from "next/head";

export type BookingResultsProps = {
  properties: PropertyCardProps[];
  initialForm?: SearchForm;
};

export const getServerSideProps: GetServerSideProps<BookingResultsProps> =
  async ({ locale, defaultLocale, query }) => {
    const directus = await getDirectusInstance();

    const check_in =
      "check_in" in query ? (query.check_in as string) : undefined;
    const check_out =
      "check_out" in query ? (query.check_out as string) : undefined;
    const guests =
      "guests" in query ? parseInt(query.guests as string, 10) : undefined;

    const propertiesResponse = await directus.transport.post(
      "/booking/availability",
      { check_in, guests, check_out }
    );

    const data = propertiesResponse.raw as PropertyAvailability[];
    const properties =
      data.map<PropertyCardProps>((x) => {
        const translated = resolveTranslation(
          locale,
          defaultLocale,
          x.translations
        );
        const output = {
          id: x.id,
          bathrooms: x.bathrooms,
          bedrooms: x.bedrooms,
          guests: x.accommodates_max,
          cover: x.featured_image,
          price: x.price ?? x.min_price ?? x.max_price,
          slug: translated.slug ?? x.id,
          title: translated.title,
        };
        return output;
      }) ?? [];

    const initialForm: SearchForm = {};

    if (check_in) initialForm.checkIn = check_in;
    if (check_out) initialForm.checkOut = check_out;
    if (guests) initialForm.guests = guests;

    return {
      props: {
        properties,
        initialForm,
        ...(await serverSideTranslations(locale ?? "en", [
          "common",
          "homes",
          "forms",
          "property",
        ])),
      },
    };
  };

export default function BookingResults({
  properties,
  initialForm,
}: BookingResultsProps) {
  const { t } = useTranslation("homes");

  const handleContact = useCallback(async (form: BookingContactFormData) => {
    if (typeof form.guests === "string") {
      form.guests = parseInt(form.guests);
    }
    const response = await fetch("/api/contact/send", {
      method: "post",
      body: JSON.stringify(form),
      headers: {
        "content-type": "application/json",
      },
    });

    return response.ok;
  }, []);

  return (
    <>
      <Head>
        <title>{t("title")} - FeelingHome</title>
      </Head>
      <ReservationsTemplate
        onContact={handleContact}
        properties={properties}
        searchInitialForm={initialForm}
      />
    </>
  );
}
